import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import HistoryApi from '../../history/history.api';
import PoleApi from '../../../basic/pole/pole.api';
import { useAppSelector } from '../../../../helpers/redux';
import { RootState } from '../../../../app/store';
import httpClient from '../../../../http-client';
import { BaseServiceClass } from 'base-service-class';

interface PoleManagementSidebarProps {
    filters?: any;
    tableData?: any[];
    exportType?: string;
    setExportType?: (type: string) => void;
    selectedExportFields?: string[];
    setSelectedExportFields?: React.Dispatch<React.SetStateAction<string[]>>;
}

const PoleManagementSidebar = ({
    filters = {},
    tableData = [],
    exportType = 'kmz',
    setExportType = () => {},
    selectedExportFields = [],
    setSelectedExportFields = () => {},
}: PoleManagementSidebarProps) => {
    const { userAuth } = useAppSelector((state: RootState) => state.user);
    const projects = useAppSelector(
        (state: RootState) => state.project?.list || [],
    );
    const districts = useAppSelector(
        (state: RootState) => state.district?.list || [],
    );
    const jobTypes = useAppSelector(
        (state: RootState) => state.jobType?.list || [],
    );
    const jobStatuses = useAppSelector(
        (state: RootState) => state.jobStatus?.list || [],
    );

    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState({
        current: 0,
        total: 0,
    });

    const exportFieldOptions = [
        { label: 'Job Number', value: 'Job Number' },
        { label: 'Pole Seq', value: 'Pole Seq' },
        { label: 'Pole No', value: 'Pole No' },
        { label: 'Is Pip', value: 'Is Pip' },
        { label: 'Pole Type', value: 'Pole Type' },
        { label: 'Pole Status', value: 'Pole Status' },
    ];

    const toggleExportField = (value: string) => {
        setSelectedExportFields((prev) =>
            prev.includes(value)
                ? prev.filter((f) => f !== value)
                : [...prev, value],
        );
    };

    const handleExport = async () => {
        if (!userAuth?.id) return;
        if (
            exportType !== 'excel' &&
            exportType !== 'kml' &&
            exportType !== 'kmz'
        ) {
            alert('Fitur ini sedang dalam pengembangan.');
            return;
        }

        setIsExporting(true);
        try {
            const requestBody: any = {};

            if (filters.startRequestDate)
                requestBody.startDate = new Date(
                    filters.startRequestDate,
                ).toISOString();
            if (filters.endRequestDate) {
                const endDate = new Date(filters.endRequestDate);
                endDate.setHours(23, 59, 59, 999);
                requestBody.endDate = endDate.toISOString();
            }

            if (filters.completeStartDate)
                requestBody.completeStartDate = new Date(
                    filters.completeStartDate,
                ).toISOString();
            if (filters.completeEndDate) {
                const completeEndDate = new Date(filters.completeEndDate);
                completeEndDate.setHours(23, 59, 59, 999);
                requestBody.completeEndDate = completeEndDate.toISOString();
            }

            if (filters.project_name) {
                const found = projects.find(
                    (p: any) => p.name === filters.project_name,
                );
                if (found) requestBody.projectId = found.id;
            }
            if (filters.district) {
                const found = districts.find(
                    (d: any) => d.name === filters.district,
                );
                if (found) requestBody.districtId = found.id;
            }
            if (filters.kind_job) {
                const found = jobTypes.find(
                    (j: any) => j.name === filters.kind_job,
                );
                if (found) requestBody.jobTypeId = found.id;
            }
            if (filters.status) {
                // const found = jobStatuses.find(
                //     (s: any) => s.name === filters.status,
                // );
                // if (found) requestBody.jobStatusId = found.id;
                requestBody.jobStatusId = filters.status;
            }
            if (filters.number_job) requestBody.number_job = filters.number_job;
            if (filters.other_number)
                requestBody.other_number = filters.other_number;
            if (filters.pole_types) requestBody.poleTypeId = filters.pole_types;
            if (filters.pole_statuses)
                requestBody.poleStatusId = filters.pole_statuses;

            const res = await httpClient.post(
                BaseServiceClass.BASE_URL +
                    '/api/Job/ExportJobPoleReportToExcel',
                requestBody,
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${userAuth.id}`,
                    },
                },
            );

            const blob = new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            if (exportType === 'excel') {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                let filename = `Pole_Management_Export_${Date.now()}.xlsx`;
                const disposition = res.headers['content-disposition'];
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    const filenameRegex =
                        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) {
                        filename = matches[1].replace(/['"]/g, '');
                    }
                }
                a.download = filename;
                a.click();
                URL.revokeObjectURL(url);
            } else if (exportType === 'kml' || exportType === 'kmz') {
                const arrayBuffer = await blob.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const allExportData = XLSX.utils.sheet_to_json<any>(worksheet);

                if (!allExportData || allExportData.length === 0) {
                    alert(
                        'Tidak ada data pole yang ditemukan dari filter tersebut.',
                    );
                    setIsExporting(false);
                    return;
                }

                let kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Pole Management Export</name>`;

                allExportData.forEach((data) => {
                    if (data.Latitude && data.Longitude) {
                        let descriptionContent = '';
                        if (selectedExportFields.includes('Job Number')) {
                            descriptionContent += `          <b>Job Number:</b> ${data['Job Number'] || data['Job Numb'] || ''}<br/>\n`;
                        }
                        if (selectedExportFields.includes('Pole Seq')) {
                            descriptionContent += `          <b>Pole Seq:</b> ${data['Pole Seq'] || ''}<br/>\n`;
                        }
                        if (selectedExportFields.includes('Pole No')) {
                            descriptionContent += `          <b>Pole No:</b> ${data['Pole No'] || data['Pole Seq'] || ''}<br/>\n`;
                        }
                        if (selectedExportFields.includes('Is Pip')) {
                            const isPipVal = data['Is Pip'];
                            const isPipStr =
                                isPipVal !== undefined && isPipVal !== null
                                    ? String(isPipVal)
                                    : '';
                            descriptionContent += `          <b>Is Pip:</b> ${isPipStr}<br/>\n`;
                        }
                        if (selectedExportFields.includes('Pole Type')) {
                            descriptionContent += `          <b>Pole Type:</b> ${data['Pole Type'] || ''}<br/>\n`;
                        }
                        if (selectedExportFields.includes('Pole Status')) {
                            descriptionContent += `          <b>Pole Status:</b> ${data['Pole Status'] || ''}<br/>\n`;
                        }

                        kmlContent += `
    <Placemark>
      <name>${data['Pole No'] || data['Pole Seq'] || 'Unknown'}</name>
      <description>
        <![CDATA[
${descriptionContent}        ]]>
      </description>
      <Point>
        <coordinates>${data.Longitude},${data.Latitude},0</coordinates>
      </Point>
    </Placemark>`;
                    }
                });

                kmlContent += `
  </Document>
</kml>`;
                if (exportType === 'kmz') {
                    const zip = new JSZip();
                    zip.file('doc.kml', kmlContent);
                    const kmzBlob = await zip.generateAsync({ type: 'blob' });
                    const url = URL.createObjectURL(kmzBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Pole_Management_Export_${Date.now()}.kmz`;
                    a.click();
                    URL.revokeObjectURL(url);
                } else {
                    const kmlBlob = new Blob([kmlContent], {
                        type: 'application/vnd.google-earth.kml+xml',
                    });
                    const url = URL.createObjectURL(kmlBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Pole_Management_Export_${Date.now()}.kml`;
                    a.click();
                    URL.revokeObjectURL(url);
                }
            }
        } catch (error) {
            console.error('Export failed', error);
            alert('Terjadi kesalahan saat melakukan export.');
        } finally {
            setIsExporting(false);
            setExportProgress({ current: 0, total: 0 });
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Edit Pole Status Filter */}
            {/* <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                        Edit Pole Status Filter{' '}
                        <span className="text-gray-400">ℹ️</span>
                    </h2>
                    <button className="text-gray-500">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 15l7-7 7 7"
                            />
                        </svg>
                    </button>
                </div>
                <div className="text-xs text-gray-500 mb-2">
                    Tampilkan Status
                </div>

                <div className="flex flex-col gap-2 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked
                            className="rounded text-[#4F46E5] focus:ring-[#4F46E5] h-4 w-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Completed
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked
                            className="rounded text-[#4F46E5] focus:ring-[#4F46E5] h-4 w-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Verified
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked
                            className="rounded text-orange-500 focus:ring-orange-500 h-4 w-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Review
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked
                            className="rounded text-purple-600 focus:ring-purple-600 h-4 w-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Pending
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked
                            className="rounded text-red-500 focus:ring-red-500 h-4 w-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            No Tag
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked
                            className="rounded text-blue-500 focus:ring-blue-500 h-4 w-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Missing GPS
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked
                            className="rounded text-[#4F46E5] focus:ring-[#4F46E5] h-4 w-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Duplicate
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            className="rounded text-gray-500 focus:ring-gray-500 h-4 w-4"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Cancelled
                        </span>
                    </label>
                </div>

                <div className="flex gap-2">
                    <button className="flex-1 border border-gray-300 dark:border-gray-600 rounded py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Select All
                    </button>
                    <button className="flex-1 border border-gray-300 dark:border-gray-600 rounded py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Clear
                    </button>
                </div>
            </div> */}

            {/* Export Data */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4">
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3">
                    Export Data
                </h2>
                <div className="text-xs text-gray-500 mb-2">Choose format</div>

                <div className="flex flex-col gap-2 mb-4">
                    <div
                        onClick={() => setExportType('kml')}
                        className={`border rounded p-2 flex gap-3 cursor-pointer transition-colors ${exportType === 'kml' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'}`}
                    >
                        <div className="text-blue-500 mt-1">
                            <svg
                                className="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                            </svg>
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                Google Earth (KML)
                            </div>
                            <div className="text-xs text-gray-500">
                                Open in Google Earth
                            </div>
                        </div>
                    </div>

                    <div
                        onClick={() => setExportType('kmz')}
                        className={`border rounded p-2 flex gap-3 cursor-pointer transition-colors ${exportType === 'kmz' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'}`}
                    >
                        <div className="text-green-500 mt-1">
                            <svg
                                className="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                            </svg>
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                Google Earth (KMZ)
                            </div>
                            <div className="text-xs text-gray-500">
                                Open in Google Earth - Compressed File
                                <br />
                                (Recommended)
                            </div>
                        </div>
                    </div>

                    <div
                        onClick={() => setExportType('excel')}
                        className={`border rounded p-2 flex gap-3 cursor-pointer transition-colors ${exportType === 'excel' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'}`}
                    >
                        <div className="text-green-600 mt-1">
                            <svg
                                className="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M21.17 3.25Q21.5 3.25 21.76 3.5 22 3.74 22 4.08V19.92Q22 20.26 21.76 20.5 21.5 20.75 21.17 20.75H7.83Q7.5 20.75 7.24 20.5 7 20.26 7 19.92V17H2.83Q2.5 17 2.24 16.76 2 16.5 2 16.17V7.83Q2 7.5 2.24 7.24 2.5 7 2.83 7H7V4.08Q7 3.74 7.24 3.5 7.5 3.25 7.83 3.25M7 13.06L8.18 15.28H9.97L8 12.06L9.93 8.89H8.22L7.13 10.9L6.09 8.89H4.29L6.18 12.06L4.25 15.28H5.96M17.42 10.66V8.66H15.42V10.66M17.42 12.66V10.66H15.42V12.66M17.42 14.66V12.66H15.42V14.66M19.42 10.66V8.66H17.42V10.66M19.42 12.66V10.66H17.42V12.66M19.42 14.66V12.66H17.42V14.66M15.42 14.66H13.42V12.66H15.42V14.66M15.42 12.66H13.42V10.66H15.42V12.66M15.42 10.66H13.42V8.66H15.42V10.66Z" />
                            </svg>
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                Excel (.xlsx)
                            </div>
                            <div className="text-xs text-gray-500">
                                Open in Excel
                            </div>
                        </div>
                    </div>
                </div>

                {(exportType === 'kml' || exportType === 'kmz') && (
                    <div className="mb-4">
                        <div className="text-xs text-gray-500 mb-2">
                            Select field for description (KML/KMZ)
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {exportFieldOptions.map((field) => (
                                <label
                                    key={field.value}
                                    className="flex items-center gap-2 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedExportFields.includes(
                                            field.value,
                                        )}
                                        onChange={() =>
                                            toggleExportField(field.value)
                                        }
                                        className="rounded text-[#03543f] focus:ring-[#03543f] h-4 w-4"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {field.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex">
                    <button
                        onClick={handleExport}
                        disabled={isExporting}
                        className="bg-[#03543f] hover:bg-[#24644e] disabled:bg-gray-400 disabled:cursor-not-allowed text-white w-full py-2 px-4 rounded-l text-sm font-medium transition-colors flex justify-center items-center"
                    >
                        {isExporting ? (
                            <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        ) : null}
                        {isExporting
                            ? exportProgress.total > 0
                                ? `Exporting... (${exportProgress.current}/${exportProgress.total})`
                                : 'Exporting...'
                            : 'Download Export'}
                    </button>
                    {/* <button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white py-2 px-3 rounded-r text-sm border-l border-indigo-400 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button> */}
                </div>
            </div>

            {/* Info Box */}
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-100 dark:border-indigo-800">
                <h3 className="text-sm font-semibold text-indigo-800 dark:text-indigo-300 mb-2">
                    Info
                </h3>
                <ul className="text-xs text-indigo-700 dark:text-indigo-400 space-y-2 list-disc pl-4">
                    <li>
                        Exported data only includes poles from Completed jobs
                        within the selected date range.
                    </li>
                    <li>
                        Exported data matches the data displayed in the table.
                        (This process may take some time depending on the amount
                        of data being exported).
                    </li>
                    <li>
                        Please validate the data before exporting to Google
                        Earth.
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default PoleManagementSidebar;
